import { type FormEvent, useEffect, useState } from 'react';
import { useAuthStore } from '@/store/authStore';
import { useOrganizations, useUpdateOrganization } from '@/hooks/useOrganizations';
import { PageSpinner } from '@/components/ui/Spinner';
import { ErrorState, EmptyState, Alert } from '@/components/ui/Alert';
import { getApiErrorMessage } from '@/api/client';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { hasRole } from '@/lib/roles';

export function AdminSettingsPage() {
  const user = useAuthStore((s) => s.user);
  const isSuperAdmin = hasRole(user, 'SuperAdmin');
  const organizations = useOrganizations({ page: 1, pageSize: 100 });

  const [orgId, setOrgId] = useState<string>(user?.organizationId ?? '');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const selectedOrg = organizations.data?.items.find((o) => o.id === orgId);
  const updateOrganization = useUpdateOrganization(orgId);

  useEffect(() => {
    if (selectedOrg) {
      setName(selectedOrg.name);
      setDescription(selectedOrg.description ?? '');
    }
  }, [selectedOrg]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!orgId) return;
    await updateOrganization.mutateAsync({ name, description });
  }

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-xl font-semibold text-fg">Nastavitve</h1>

      <Card className="max-w-lg">
        <CardHeader>
          <h2 className="font-semibold text-fg">Nastavitve organizacije</h2>
          <p className="text-sm text-fg-muted">Posodobite podatke za svojo organizacijo.</p>
        </CardHeader>
        <CardBody>
          {organizations.isLoading && <PageSpinner />}
          {organizations.isError && (
            <ErrorState message={getApiErrorMessage(organizations.error)} onRetry={() => organizations.refetch()} />
          )}
          {organizations.isSuccess && organizations.data.items.length === 0 && (
            <EmptyState title="Ni razpoložljivih organizacij" />
          )}
          {organizations.isSuccess && organizations.data.items.length > 0 && (
            <form onSubmit={handleSubmit} className="flex flex-col gap-3">
              {updateOrganization.isError && (
                <Alert variant="error">{getApiErrorMessage(updateOrganization.error)}</Alert>
              )}
              {updateOrganization.isSuccess && <Alert variant="success">Nastavitve shranjene.</Alert>}

              {isSuperAdmin && (
                <Select label="Organizacija" value={orgId} onChange={(e) => setOrgId(e.target.value)}>
                  <option value="">Izberite organizacijo&hellip;</option>
                  {organizations.data.items.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </Select>
              )}

              {orgId && (
                <>
                  <Input label="Naziv" value={name} onChange={(e) => setName(e.target.value)} />
                  <Input
                    label="Opis"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                  <Button type="submit" isLoading={updateOrganization.isPending} className="self-start">
                    Shrani nastavitve
                  </Button>
                </>
              )}
            </form>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
